import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Camera, Star, Quote, X } from 'lucide-react';
import { photographerAPI, galleryAPI } from '../services/api';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [photographerInfo, setPhotographerInfo] = useState(null);
  const [featuredImages, setFeaturedImages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExpertiseImage, setSelectedExpertiseImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [infoResponse, imagesResponse, testimonialsResponse] = await Promise.all([
        photographerAPI.getInfo(),
        galleryAPI.getImages('all'),
        axios.get(`${BACKEND_URL}/api/testimonials`)
      ]);
      
      setPhotographerInfo(infoResponse.info);
      // Get first 3 images as featured
      setFeaturedImages(imagesResponse.slice(0, 3));
      setTestimonials(testimonialsResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      // Fallback to default values
      setPhotographerInfo({
        name: 'Franziska Daum',
        tagline: 'Capturing Moments, Creating Memories',
        short_bio: 'Professionelle Hobby-Fotografin',
        bio: 'Professionelle Hobby-Fotografin'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={16}
            className={`${
              star <= rating 
                ? 'text-yellow-400 fill-yellow-400' 
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  if (loading || !photographerInfo) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Camera size={48} className="mx-auto mb-4 text-gray-400 animate-pulse" />
          <p className="text-gray-600">Wird geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-spacing-large bg-gray-50">
        <div className="photo-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-up">
              <div className="flex items-center gap-2 mb-6">
                <Camera size={24} className="text-primary" />
                <span className="text-sm uppercase tracking-wider font-medium">Fotografie</span>
              </div>
              <h1 className="hero-title mb-6">
                {photographerInfo.tagline}
              </h1>
              <p className="subtitle mb-8">
                {photographerInfo.short_bio}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/galerie" className="btn-primary">
                  Portfolio ansehen
                </Link>
                <Link to="/kontakt" className="btn-outline">
                  Kontakt aufnehmen
                </Link>
              </div>
            </div>
            <div className="fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="relative p-6 rounded-lg" style={{ backgroundColor: '#4a6d8c', background: 'linear-gradient(135deg, #4a6d8c 0%, #2c4a62 50%, #1a3a4a 100%)' }}>
                <img 
                  src={photographerInfo.hero_image_url || "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=800&q=80"} 
                  alt="Photography Equipment"
                  className="w-full h-auto object-contain rounded-lg shadow-2xl"
                />
                {photographerInfo.stat_experience && photographerInfo.stat_experience.trim() !== '' && photographerInfo.stat_experience.trim() !== '0' && (
                  <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-lg shadow-xl">
                    <p className="text-4xl font-bold text-primary">{photographerInfo.stat_experience}</p>
                    <p className="text-sm text-gray-600 mt-1">Jahre Erfahrung</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Work */}
      <section className="section-spacing">
        <div className="photo-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Ausgewählte Arbeiten</h2>
            <p className="subtitle max-w-2xl mx-auto">
              Eine Auswahl meiner besten Fotografien aus verschiedenen Kategorien
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredImages.length > 0 ? (
              featuredImages.map((image, index) => (
                <div 
                  key={image.id} 
                  className="photo-card fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${image.url}`} alt={image.title} />
                  <div className="photo-overlay">
                    <h3 className="font-heading text-xl font-semibold">{image.title}</h3>
                    <p className="text-sm mt-2 uppercase tracking-wider">{image.category}</p>
                  </div>
                  {/* Watermark */}
                  <div className="absolute bottom-4 right-4 text-white text-xs opacity-50 font-semibold">
                    © Franziska Daum
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-3 text-center py-12">
                <p className="text-gray-500">Keine Bilder verfügbar</p>
              </div>
            )}
          </div>
          <div className="text-center mt-12">
            <Link to="/galerie" className="btn-primary inline-flex items-center gap-2">
              Vollständige Galerie
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Preview / Expertise */}
      {photographerInfo.expertise_items && photographerInfo.expertise_items.length > 0 ? (
        <section className="section-spacing bg-gray-50">
          <div className="photo-container">
            <h2 className="section-title text-center">{photographerInfo.expertise_title || 'Meine Expertise'}</h2>
            <div className={`grid grid-cols-1 ${photographerInfo.expertise_items.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : 'md:grid-cols-3'} gap-8`}>
              {photographerInfo.expertise_items.map((item, index) => (
                <div key={index} className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  {item.image_url ? (
                    <div className="w-24 h-24 rounded-full mx-auto mb-6 overflow-hidden border-4 border-primary cursor-pointer hover:scale-105 transition-transform"
                         onClick={() => setSelectedExpertiseImage(item.image_url)}>
                      <img 
                        src={item.image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}` : item.image_url}
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-24 h-24 bg-primary text-white rounded-full flex items-center justify-center mb-6 mx-auto">
                      <Camera size={36} />
                    </div>
                  )}
                  <h3 className="font-heading text-2xl font-semibold text-center mb-4">{item.title}</h3>
                  <p className="body-text text-gray-600 text-center">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <section className="section-spacing bg-gray-50">
          <div className="photo-container">
            <h2 className="section-title text-center">{photographerInfo.expertise_title || 'Meine Expertise'}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Camera size={28} />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-center mb-4">Portraits</h3>
                <p className="body-text text-gray-600 text-center">
                  Portraetfotografie fuer Business, Familie und persoenliche Anlaesse
                </p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Camera size={28} />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-center mb-4">Landschaft</h3>
                <p className="body-text text-gray-600 text-center">
                  Kunstvolle Naturaufnahmen und Landschaftsbilder fuer Ihr Zuhause
                </p>
              </div>
              <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center mb-6 mx-auto">
                  <Camera size={28} />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-center mb-4">Events</h3>
                <p className="body-text text-gray-600 text-center">
                  Festhalten besonderer Momente bei Hochzeiten, Geburtstagen und Events
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="section-spacing">
          <div className="photo-container">
            <div className="text-center mb-12">
              <h2 className="section-title">Was meine Kunden sagen</h2>
              <p className="subtitle max-w-2xl mx-auto">
                Feedback von zufriedenen Kunden
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.slice(0, 3).map((testimonial, index) => (
                <div 
                  key={testimonial.id}
                  className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <Quote size={32} className="text-primary opacity-30 mb-4" />
                  <p className="body-text text-gray-600 italic mb-6">
                    "{testimonial.text}"
                  </p>
                  <div className="border-t pt-4">
                    <div className="mb-2">
                      {renderStars(testimonial.rating)}
                    </div>
                    <p className="font-semibold text-gray-900">{testimonial.author}</p>
                    {testimonial.location && (
                      <p className="text-sm text-gray-500">{testimonial.location}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link to="/bewertung-abgeben" className="btn-outline inline-flex items-center gap-2">
                Eigene Bewertung abgeben
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section-spacing">
        <div className="photo-container">
          <div className="bg-primary text-white rounded-2xl p-12 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-6">
              Bereit für Ihr nächstes Fotoshooting?
            </h2>
            <p className="subtitle text-gray-300 mb-8 max-w-2xl mx-auto">
              Kontaktieren Sie mich für ein unverbindliches Gespräch über Ihr Projekt
            </p>
            <Link to="/kontakt" className="btn-outline border-white text-white hover:bg-white hover:text-primary">
              Jetzt anfragen
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal for Expertise Images */}
      {selectedExpertiseImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedExpertiseImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setSelectedExpertiseImage(null)}
          >
            <X size={32} />
          </button>
          <img 
            src={selectedExpertiseImage.startsWith('/api') ? `${BACKEND_URL}${selectedExpertiseImage}` : selectedExpertiseImage}
            alt="Expertise"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default Home;
